import { formatDateToYYYYMMDDKorean } from "@/common/util/date-util";
import {
  formatCommasNumber,
  formatNumberWithCommas,
} from "@/common/util/format-number-with-commas";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import {
  Document,
  Font,
  Page,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

interface Props {
  orderDetail: OrderDetailDto;
  children?: React.ReactNode;
}

const MINIMUM_ROWS = 20;

// Noto Sans CJK 한글 폰트 추가 (Base64 또는 URL로 제공)
Font.register({
  family: "NotoSans",
  fonts: [
    { src: "/font/NotoSansKR-Regular.ttf", fontWeight: 400 },
    { src: "/font/NotoSansKR-Bold.ttf", fontWeight: 700 },
  ], // Noto Sans CJK 폰트
});

// 스타일 정의
const styles = StyleSheet.create({
  page: {
    fontFamily: "NotoSans",
    border: 5,
  },
  title: {
    fontSize: 23,
    fontWeight: "bold",
    textAlign: "center",
    paddingTop: 10,
  },
  table: {
    fontSize: 10,
    display: "flex",
    width: "100%",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColFull: {
    fontSize: 10,
    borderStyle: "solid",
    borderRight: 1,
    padding: 4,
    display: "flex",
    justifyContent: "center",
    gap: 5,
  },
  tableColHeader: {
    width: 60,
    borderStyle: "solid",
    borderBottom: 1,
    padding: 4,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  tableColSmallHeader: {
    width: 30,
    borderStyle: "solid",
    borderLeft: 1,
    borderBottom: 1,
    padding: 4,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  tableColEmpty: {
    width: 272,
    borderStyle: "solid",
    borderBottom: 1,
    borderLeft: 1,
    padding: 4,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  tableColNotEmpty: {
    width: 121,
    borderStyle: "solid",
    borderBottom: 1,
    borderLeft: 1,
    padding: 4,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  mainTableCol1: { flex: 4, borderBottom: 1, borderRight: 1, padding: 4 },
  mainTableCol2: { flex: 1, borderBottom: 1, borderRight: 1, padding: 4 },
  mainTableCol3: { flex: 1, borderBottom: 1, borderRight: 1, padding: 4 },
  mainTableCol4: { flex: 4, borderBottom: 1, borderRight: 1, padding: 4 },
  mainTableCol5: { flex: 3, borderBottom: 1, borderRight: 1, padding: 4 },
  mainTableCol6: { flex: 3, borderBottom: 1, padding: 4 },

  mainTableRow1: {
    flex: 4,
    borderBottom: 1,
    borderRight: 1,
    padding: "2px 4px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  mainTableRow2: {
    flex: 1,
    borderBottom: 1,
    borderRight: 1,
    padding: "2px 4px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  mainTableRow3: {
    flex: 1,
    borderBottom: 1,
    borderRight: 1,
    padding: "2px 4px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  mainTableRow4: {
    flex: 4,
    borderBottom: 1,
    borderRight: 1,
    padding: "2px 4px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  mainTableRow5: {
    flex: 3,
    borderBottom: 1,
    borderRight: 1,
    padding: "2px 4px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  mainTableRow6: {
    flex: 3,
    borderBottom: 1,
    padding: "2px 4px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  resultRow1: {
    flex: 4,
    borderBottom: 1,
    borderRight: 1,
    padding: "6px 4px",
  },
  resultRow2: {
    flex: 1,
    borderBottom: 1,
    borderRight: 1,
    padding: "6px 4px",
  },
  resultRow3: {
    flex: 1,
    borderBottom: 1,
    borderRight: 1,
    padding: "6px 4px",
  },
  resultRow4: {
    flex: 4,
    borderBottom: 1,
    borderRight: 1,
    padding: "6px 4px",
  },
  resultRow5: {
    flex: 3,
    borderBottom: 1,
    borderRight: 1,
    padding: "6px 4px",
  },
  resultRow6: { flex: 3, borderBottom: 1, padding: "6px 4px" },
});

// PDF 문서 컴포넌트
const MyDocument: React.FC<Props> = ({ orderDetail }) => {
  const processedItems =
    orderDetail.cartItems.length >= MINIMUM_ROWS
      ? orderDetail.cartItems
      : [
          ...orderDetail.cartItems,
          ...Array(MINIMUM_ROWS - orderDetail.cartItems.length)
            .fill(null)
            .map((_, index) => ({
              id: 0,
              index: orderDetail.cartItems.length + index,
              name: "-",
              erpCode: "-",
              price: 0,
              quantity: 0,
              originalPrice: 0,
              discountRate: 0,
              gradeDiscountRate: 0,
              discountPrice: 0,
              incentiveDiscountRate: 0,
              incentiveDiscountPrice: 0,
              totalPrice: 0,
              vat: 0,
              createdAt: "",
              updatedAt: "",
            })),
        ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* 제목 */}
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: 20,
          }}
        >
          <View style={{ display: "flex", flex: 1, marginLeft: 10 }}>
            <Text style={{ fontSize: 10, marginBottom: 10 }}>
              NO. {orderDetail.invoiceNumber}
            </Text>
            <View
              style={{ backgroundColor: "black", height: "0.5px", width: 150 }}
            />
          </View>
          <Text style={{ ...styles.title, flex: 1 }}>거래명세표</Text>
          <View style={{ flex: 1 }} />
        </View>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: 40,
            borderBottom: 1,
          }}
        >
          <View style={{ flex: 2, marginLeft: 10 }}>
            <Text style={{ fontSize: 10, marginTop: 5 }}>
              {formatDateToYYYYMMDDKorean(orderDetail.createdAt)}
            </Text>
            <View
              style={{
                display: "flex",
                alignItems: "flex-end",
                width: "100%",
                paddingRight: 30,
              }}
            >
              <Text style={{ fontSize: 10, marginTop: 30 }}>
                {orderDetail.ordererName} 귀하
              </Text>
            </View>
            <View
              style={{
                backgroundColor: "black",
                height: "0.5px",
                width: 200,
              }}
            />
            <Text style={{ fontSize: 11, marginTop: 38 }}>
              아래와 같이 계산
            </Text>
          </View>

          <View
            style={{
              flex: 3,
              display: "flex",
              flexDirection: "row",
              borderStyle: "solid",
              borderWidth: 1,
              borderBottom: "none",
            }}
          >
            <View style={styles.tableRow}>
              <View style={styles.tableColFull}>
                <Text>공</Text>
                <Text>급</Text>
                <Text>자</Text>
              </View>
            </View>
            <View style={styles.table}>
              {/* 등록번호 row */}
              <View style={styles.tableRow}>
                <Text style={styles.tableColHeader}>등록번호</Text>
                <Text style={styles.tableColEmpty}>585-88-01527</Text>
              </View>

              {/* 상호(법인명) row */}
              <View style={styles.tableRow}>
                <Text style={styles.tableColHeader}>상호(법인명)</Text>
                <Text style={styles.tableColNotEmpty}>
                  아카라라이프 주식회사
                </Text>
                <Text style={styles.tableColSmallHeader}>성명</Text>
                <Text style={styles.tableColNotEmpty}>김현철</Text>
              </View>

              {/* 사업장주소 row */}
              <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                  <Text>사업장주소</Text>
                </View>
                <Text style={styles.tableColEmpty}>
                  서울 서초구 강남대로53길 8 스타크 강남빌딩
                  마이워크스페이스타워 4층
                </Text>
              </View>

              {/* 업태 row */}
              <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                  <Text>업태</Text>
                </View>
                <View style={styles.tableColNotEmpty}>
                  <Text>소프트웨어 개발 및 공급업</Text>
                </View>
                <View style={styles.tableColSmallHeader}>
                  <Text>종목</Text>
                </View>
                <Text style={styles.tableColNotEmpty}>
                  응용 소프트웨어 개발 및 공급업
                </Text>
              </View>

              {/* 전화번호 row */}
              <View style={styles.tableRow}>
                <Text
                  style={{ ...styles.tableColHeader, borderBottom: "none" }}
                >
                  전화번호
                </Text>
                <Text style={{ ...styles.tableColEmpty, borderBottom: "none" }}>
                  02-6949-2662
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
            fontSize: 11,
            padding: "10px 0",
            borderBottom: 1,
          }}
        >
          <Text style={{ marginLeft: 10 }}>총 금액 (VAT 포함)</Text>
          <Text style={{ marginRight: 8 }}>
            {formatCommasNumber(orderDetail.finalPriceSum)}원
          </Text>
        </View>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            fontSize: 10,
            textAlign: "center",
          }}
        >
          <Text style={styles.mainTableCol1}>품 목 명</Text>
          <Text style={styles.mainTableCol2}>규 격</Text>
          <Text style={styles.mainTableCol3}>수 량</Text>
          <Text style={styles.mainTableCol4}>공 급 가 액</Text>
          <Text style={styles.mainTableCol5}>세 액</Text>
          <Text style={styles.mainTableCol6}>판매 금액</Text>
        </View>

        {processedItems.map((item) => {
          const data = {
            name: item.name,
            spec: "-",
            qty: item.quantity || "-",
          };

          return (
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                fontSize: 10,
                textAlign: "center",
              }}
            >
              <View style={styles.mainTableRow1}>
                <Text>{data.name}</Text>
              </View>
              <View style={styles.mainTableRow2}>
                <Text>-</Text>
              </View>
              <View style={styles.mainTableRow3}>
                <Text>{data.qty}</Text>
              </View>
              <View style={styles.mainTableRow4}>
                <Text>
                  {formatNumberWithCommas(item.totalPrice - item.vat)}
                </Text>
              </View>
              <View style={styles.mainTableRow5}>
                <Text>{formatNumberWithCommas(item.vat)}</Text>
              </View>
              <View style={styles.mainTableRow6}>
                <Text>{formatNumberWithCommas(item.totalPrice)}</Text>
              </View>
            </View>
          );
        })}

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            fontSize: 10,
            textAlign: "center",
          }}
        >
          <Text style={styles.resultRow1}>계</Text>
          <Text style={styles.resultRow2}></Text>
          <Text style={styles.resultRow3}></Text>
          <Text style={styles.resultRow4}>
            {orderDetail.totalPriceSum - orderDetail.vatSum}
          </Text>
          <Text style={styles.resultRow5}>{orderDetail.vatSum}</Text>
          <Text style={styles.resultRow6}>{orderDetail.totalPriceSum}</Text>
        </View>

        {/* 하단 설명 */}
        <View style={{ fontSize: 10, padding: 10, borderBottom: 1 }}>
          <Text>* 산출기준</Text>
          <Text>
            - 세액 = 판매 금액 * 10/110 (세율 10%기준, 소수점 이하 올림)
          </Text>
          <Text>- 공급가액 = 판매 금액 - 부가세</Text>
        </View>
      </Page>
    </Document>
  );
};

// PDF 미리보기 및 다운로드 링크 컴포넌트
export const InvoicePDFDownloadButton: React.FC<Props> = ({
  orderDetail,
  children,
}) => {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      {/* PDF 미리보기 */}
      {/* <PDFViewer
        style={{ width: "80%", height: "600px", marginBottom: "20px" }}
      >
        <MyDocument orderDetail={orderDetail} />
      </PDFViewer> */}

      {/* PDF 다운로드 링크 */}
      <PDFDownloadLink
        document={<MyDocument orderDetail={orderDetail} />}
        fileName="거래명세표.pdf"
      >
        {children}
      </PDFDownloadLink>
    </div>
  );
};
